import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { UUID } from "@bitory-gmbh/nidavellir";
import { TextInput } from "@/shared/components/form/text-input";
import {
  updateAhuDataCache,
  useHtzAirHandlingUnitDataPutMutation,
  useHtzAirHandlingUnitDataShowQuery,
} from "../api/ahu-data-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { cn } from "@/shared/lib/utils";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardSkeleton } from "@/shared/components/card-skeleton";

export function AhuDataBaseDataCard({
  id,
  hideLabel,
  disabled,
}: {
  id: UUID;
  hideLabel?: boolean;
  disabled?: boolean;
}) {
  const { data, error, isLoading } = useHtzAirHandlingUnitDataShowQuery({ id });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  // we make sure that the child will get a value from the RTK Query Cache
  if (!data) return;

  return (
    <AhuDataBaseDataCardContent
      id={id}
      disabled={disabled}
      hideLabel={hideLabel}
    />
  );
}

function AhuDataBaseDataCardContent({
  id,
  hideLabel,
  disabled,
}: {
  id: UUID;
  hideLabel?: boolean;
  disabled?: boolean;
}) {
  const { data } = useHtzAirHandlingUnitDataShowQuery({ id });
  const [set, { error }] = useOptimisticDebouncedMutation(
    data!,
    updateAhuDataCache,
    useHtzAirHandlingUnitDataPutMutation(),
    { resetOnError: false },
  );

  if (!data) return;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("RLT-Anlage: ")}
          {data.designation}
        </CardTitle>
      </CardHeader>
      <CardContent className="@container">
        <div className="grid gap-3 @md:grid-cols-2">
          <TextInput
            label={t("Label")}
            field="label"
            text={data.label}
            onTextChange={(label) => set({ ...data, label })}
            errors={inputValidationErrorMessages(error, "label")}
            disabled={disabled}
            className={cn(hideLabel && "hidden")}
          />
          <TextInput
            label={t("Bezeichnung")}
            field="designation"
            text={data.designation}
            onTextChange={(designation) => set({ ...data, designation })}
            errors={inputValidationErrorMessages(error, "designation")}
            disabled={disabled}
          />
          <TextInput
            label={t("Standort")}
            field="location"
            text={data.location}
            onTextChange={(location) => set({ ...data, location })}
            errors={inputValidationErrorMessages(error, "location")}
            disabled={disabled}
          />
          <TextInput
            label={t("Baujahr")}
            field="constructionYear"
            text={data.constructionYear}
            onTextChange={(constructionYear) =>
              set({ ...data, constructionYear })
            }
            errors={inputValidationErrorMessages(error, "constructionYear")}
            disabled={disabled}
          />
          <TextInput
            label={t("Luftstrom m³/h")}
            field="volumeFlowRate"
            text={data.volumeFlowRate}
            onTextChange={(volumeFlowRate) => set({ ...data, volumeFlowRate })}
            errors={inputValidationErrorMessages(error, "volumeFlowRate")}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}
